/**
 * App roles (ADR-034): one app's roles in one workspace — the Access screen's
 * data and writes.
 *
 * Every write invalidates the whole (workspace, app) family, because a grant
 * changes the screen, the caller's own access and the explainer at once.
 *
 * Every write that grants a role or records an acceptance names the policy
 * the screen showed (`expectedAccessPolicyId`, the screen's `policyId`): a
 * publish between the look and the write refuses it (`ACCESS_POLICY_CHANGED`,
 * 409) rather than accept permissions nobody saw, and the screen is reloaded
 * so the person sees what changed.
 *
 * A write that meets another change to the same app's access holding its
 * lock is refused `ACCESS_BUSY` with nothing written; it is tried once more
 * after a short pause, then the refusal is shown. Writes carrying a step-up
 * proof are not retried: the person confirms again (a one-time code may not
 * be used twice).
 *
 * SECURITY: the step-up writes (proposing or approving a sensitive role,
 * accepting a sensitive role's new permissions, turning a safeguard off) send
 * the person's password and TOTP code, so they go through the untraced
 * `stepUpFetch` — never the traced `api` client, whose request log the
 * report-a-problem dialog exposes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  appAccessPath,
  APP_ACCESS_BULK_GRANT_MAX,
  type AppAccessActivationSetupDto,
  type AppAccessSettingsDto,
  type AppRoleAssignmentDto,
  type AppRoleProposalDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { stepUpFetch } from '@/lib/step-up-fetch';
import type { StepUpCredentials } from '../components/StepUpDialog';

export interface AppAccessScope {
  holderId: string;
  archeId: string;
}

const proof = (creds: StepUpCredentials) => ({
  currentPassword: creds.currentPassword,
  ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
});

function useInvalidateAppAccess({ holderId, archeId }: AppAccessScope) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.vault.appAccessAll(holderId, archeId) });
}

/**
 * Whether a write was refused because the app's roles changed since the
 * screen loaded (a publish between the look and the write).
 */
export function isAccessPolicyChanged(err: unknown): boolean {
  return errorCodeOf(err) === 'ACCESS_POLICY_CHANGED';
}

/** The error code a write was refused with (on the error, or leading its message). */
function errorCodeOf(err: unknown): string | null {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string') return code;
  const message = err instanceof Error ? err.message : '';
  return /^([A-Z][A-Z0-9_]+):/.exec(message)?.[1] ?? null;
}

/** The pause before the one automatic retry of a write refused `ACCESS_BUSY`. */
export const ACCESS_BUSY_RETRY_MS = 800;

/**
 * Run a governance write, trying it once more after ACCESS_BUSY_RETRY_MS when
 * the server answers `ACCESS_BUSY` — another change to this app's access held
 * the lock, and nothing was written, so a second try is safe. A second
 * `ACCESS_BUSY` goes to the caller, which says so.
 */
export async function retryOnceIfBusy<T>(write: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } catch (err) {
    if (errorCodeOf(err) !== 'ACCESS_BUSY') throw err;
    await new Promise((resolve) => setTimeout(resolve, ACCESS_BUSY_RETRY_MS));
    return write();
  }
}

/** A mutation function that retries once on `ACCESS_BUSY` (see retryOnceIfBusy). */
function busyRetrying<A, R>(write: (input: A) => Promise<R>): (input: A) => Promise<R> {
  return (input) => retryOnceIfBusy(() => write(input));
}

/** `{ expectedAccessPolicyId }` when the screen showed a policy, else nothing. */
const expected = (policyId: string | null | undefined) =>
  policyId ? { expectedAccessPolicyId: policyId } : {};

/** The Access screen (managers and reviewers; anyone else gets a 404). */
export function useAppAccessScreen(scope: AppAccessScope, enabled = true) {
  return useQuery({
    queryKey: queryKeys.vault.appAccess(scope.holderId, scope.archeId),
    queryFn: () => api.getAppAccess(scope.holderId, scope.archeId),
    enabled,
    retry: false,
  });
}

/** The caller's own access to the app here. */
export function useMyAppAccess(scope: AppAccessScope) {
  return useQuery({
    queryKey: queryKeys.vault.myAppAccess(scope.holderId, scope.archeId),
    queryFn: () => api.getMyAppAccess(scope.holderId, scope.archeId),
    retry: false,
  });
}

/** What the access decision answers for one person. */
export function useAppAccessExplanation(scope: AppAccessScope, principalId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.appAccessExplain(scope.holderId, scope.archeId, principalId ?? ''),
    queryFn: () => api.explainAppAccess(scope.holderId, scope.archeId, principalId as string),
    enabled: principalId !== null,
    retry: false,
  });
}

export function useAppAccessReview(scope: AppAccessScope, enabled = true) {
  return useQuery({
    queryKey: queryKeys.vault.appAccessReview(scope.holderId, scope.archeId),
    queryFn: async () => (await api.getAppAccessReview(scope.holderId, scope.archeId)).review,
    enabled,
    retry: false,
  });
}

/** What turning roles on did (a sensitive seed may wait for a second person). */
export interface ActivateAppRolesResultDto {
  assignments: AppRoleAssignmentDto[];
  proposals: AppRoleProposalDto[];
  /**
   * How many roles `seedSuggested` gave from the app's suggestions, beyond
   * `assignments` (which a large workspace would make too long to send).
   */
  seededFromSuggestions?: number;
  singleApproverGrants: Array<{
    assignment: AppRoleAssignmentDto;
    reason: AppRoleSingleApproverReasonDto;
  }>;
  /**
   * Where turning roles on stands (ADR-034 phase 2): `roles` when done, or
   * `activating` while the app's existing records still need placing. Absent
   * from older servers.
   */
  setup?: AppAccessActivationSetupDto;
}

/**
 * Turn the app's roles on. A seed with a sensitive role carries the owner's
 * step-up proof, so it goes through the untraced `stepUpFetch`; one without
 * goes through `api`. `seedSuggested` — a workspace whose suggested seed is
 * over APP_ACCESS_ACTIVATION_SEED_MAX — asks the server to give everyone the
 * roles the app suggests for their tier, in place of `assignments`.
 */
export function useActivateAppRoles(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      assignments: Array<{ principalId: string; roleId: string }>;
      seedSuggested?: boolean;
      /** Where records written before roles were on go (ADR-034 phase 2). */
      placements?: Record<string, string>;
      /** The policy the screen showed. */
      expectedAccessPolicyId?: string | null;
      creds?: StepUpCredentials;
    }): Promise<ActivateAppRolesResultDto> => {
      const placements =
        input.placements && Object.keys(input.placements).length > 0
          ? { placements: input.placements }
          : {};
      const seed = input.seedSuggested
        ? { seedSuggested: true }
        : { assignments: input.assignments };
      const policy = expected(input.expectedAccessPolicyId);
      if (input.creds) {
        return stepUpFetch<ActivateAppRolesResultDto>(
          `${appAccessPath(scope.holderId, scope.archeId)}/activate`,
          { method: 'POST', body: { ...seed, ...placements, ...policy, ...proof(input.creds) } },
        );
      }
      const options = { ...(input.seedSuggested ? { seedSuggested: true } : {}), ...policy };
      return retryOnceIfBusy(() =>
        Object.keys(options).length > 0
          ? api.activateAppRoles(
              scope.holderId,
              scope.archeId,
              input.seedSuggested ? [] : input.assignments,
              input.placements,
              options,
            )
          : api.activateAppRoles(
              scope.holderId,
              scope.archeId,
              input.assignments,
              input.placements,
            ),
      ) as Promise<ActivateAppRolesResultDto>;
    },
    // Settled, not only succeeded: a failure after the server committed (a
    // dropped response, a step-up dialog closed late) must still show that
    // roles are on, or a retry meets ROLES_ALREADY_ACTIVE with a stale screen.
    onSettled: async () => {
      // The approvals list says whether roles are set up here.
      await Promise.all([
        invalidate(),
        qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(scope.holderId) }),
      ]);
    },
  });
}

/**
 * Finish turning roles on (ADR-034 phase 2): choose where the app's existing
 * records go, and place them. Also the retry after a failed placement.
 */
export function useCompleteAppRolesActivation(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: busyRetrying((placements?: Record<string, string>) =>
      api.completeAppRolesActivation(scope.holderId, scope.archeId, placements),
    ),
    onSettled: async () => {
      await Promise.all([
        invalidate(),
        qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(scope.holderId) }),
      ]);
    },
  });
}

/**
 * Show a retired audience's records to a live audience (ADR-034 phase 2) —
 * owners only, and it widens who reads them, so it carries the owner's
 * step-up proof through the untraced `stepUpFetch`.
 */
export function useRedisclose(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: (input: {
      /** Several at once, so records that point at each other move together. */
      /** `from: null` — records in no audience (roles on, written before). */
      moves: Array<{ collection: string; from: string | null; to: string }>;
      creds: StepUpCredentials;
    }): Promise<{
      moved: Array<{ collection: string; from: string | null; to: string; count: number }>;
    }> =>
      stepUpFetch(`${appAccessPath(scope.holderId, scope.archeId)}/audiences/redisclose`, {
        method: 'POST',
        body: { moves: input.moves, ...proof(input.creds) },
      }),
    onSuccess: invalidate,
  });
}

export function useGrantAppRole(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying(
      ({
        expectedAccessPolicyId,
        ...grant
      }: {
        principalId: string;
        roleId: string;
        expiresAt?: string;
        expectedAccessPolicyId?: string | null;
      }) =>
        api.grantAppRole(scope.holderId, scope.archeId, {
          ...grant,
          ...expected(expectedAccessPolicyId),
        }),
    ),
    onSuccess: invalidate,
    onError: (err) => {
      if (isAccessPolicyChanged(err)) void invalidate();
    },
  });
}

/**
 * A grant to many people that stopped part-way: the requests before
 * `failure` went through (`done`), and `notReached` were never asked for —
 * in the order they were chosen, so trying again carries on from them.
 */
export class BulkGrantStoppedError extends Error {
  constructor(
    readonly failure: unknown,
    readonly done: { assignments: AppRoleAssignmentDto[]; alreadyAssigned: string[] },
    readonly notReached: string[],
  ) {
    super(failure instanceof Error ? failure.message : 'The grant stopped part-way');
    this.name = 'BulkGrantStoppedError';
  }
}

/**
 * One non-sensitive role to many people (people who hold it are skipped).
 * One write names at most APP_ACCESS_BULK_GRANT_MAX people (the server's
 * cap), so more go as several, one after another; `onProgress` hears each.
 * The first failure stops it: nothing is granted if it was the first
 * request, otherwise it rejects with a BulkGrantStoppedError saying who was
 * reached. The screen is refreshed either way — part of it may have landed.
 */
export function useGrantAppRoleToMany(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: async ({
      principalIds,
      roleId,
      expiresAt,
      expectedAccessPolicyId,
      onProgress,
    }: {
      principalIds: string[];
      roleId: string;
      expiresAt?: string;
      /** The policy the screen showed — every request names it. */
      expectedAccessPolicyId?: string | null;
      onProgress?: (reached: number, total: number) => void;
    }) => {
      const done = { assignments: [] as AppRoleAssignmentDto[], alreadyAssigned: [] as string[] };
      for (let at = 0; at < principalIds.length; at += APP_ACCESS_BULK_GRANT_MAX) {
        const batch = principalIds.slice(at, at + APP_ACCESS_BULK_GRANT_MAX);
        try {
          const result = await retryOnceIfBusy(() =>
            api.grantAppRoleToMany(scope.holderId, scope.archeId, {
              principalIds: batch,
              roleId,
              ...(expiresAt ? { expiresAt } : {}),
              ...expected(expectedAccessPolicyId),
            }),
          );
          done.assignments.push(...result.assignments);
          done.alreadyAssigned.push(...result.alreadyAssigned);
        } catch (err) {
          if (at === 0) throw err;
          throw new BulkGrantStoppedError(err, done, principalIds.slice(at));
        }
        onProgress?.(Math.min(at + batch.length, principalIds.length), principalIds.length);
      }
      return done;
    },
    onSettled: invalidate,
  });
}

export function useRevokeAppRole(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((assignmentId: string) =>
      api.revokeAppRole(scope.holderId, scope.archeId, assignmentId),
    ),
    onSuccess: invalidate,
  });
}

export function useCloseAppRoleProposal(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((proposalId: string) =>
      api.closeAppRoleProposal(scope.holderId, scope.archeId, proposalId),
    ),
    onSuccess: invalidate,
  });
}

export function useRequestAppAccess(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((body: { roleId?: string; message?: string }) =>
      api.requestAppAccess(scope.holderId, scope.archeId, body),
    ),
    onSuccess: invalidate,
  });
}

export function useCloseAppAccessRequest(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((requestId: string) =>
      api.closeAppAccessRequest(scope.holderId, scope.archeId, requestId),
    ),
    onSuccess: invalidate,
  });
}

export function useAppointAppAccessRole(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying(
      (body: { principalId: string; kind: 'manager' | 'reviewer'; allApps: boolean }) =>
        api.appointAppAccessRole(scope.holderId, {
          principalId: body.principalId,
          kind: body.kind,
          archeId: body.allApps ? null : scope.archeId,
        }),
    ),
    onSuccess: invalidate,
  });
}

export function useEndAppAccessAppointment(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((appointmentId: string) =>
      api.endAppAccessAppointment(scope.holderId, appointmentId),
    ),
    onSuccess: invalidate,
  });
}

export function useStartAppAccessReview(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((_: void) => api.startAppAccessReview(scope.holderId, scope.archeId)),
    onSuccess: invalidate,
  });
}

export function useDecideAppAccessReviewItem(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying(
      (body: { reviewId: string; itemId: string; decision: 'keep' | 'remove' }) =>
        api.decideAppAccessReviewItem(
          scope.holderId,
          scope.archeId,
          body.reviewId,
          body.itemId,
          body.decision,
        ),
    ),
    onSuccess: invalidate,
  });
}

export function useCompleteAppAccessReview(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying((reviewId: string) =>
      api.completeAppAccessReview(scope.holderId, scope.archeId, reviewId),
    ),
    onSuccess: invalidate,
  });
}

// ── Step-up writes: untraced, with the person's proof ────────────────────────

/** Why a sensitive role was granted by one person: an owner acting alone, or nobody else to approve. */
export type AppRoleSingleApproverReasonDto = 'owner' | 'no_second_person';

/** Propose a sensitive role — or, as an owner, grant it alone (the server says which). */
export async function proposeSensitiveAppRole(
  scope: AppAccessScope,
  body: { principalId: string; roleId: string; expiresAt?: string },
  creds: StepUpCredentials,
  /** The policy the screen showed. */
  expectedAccessPolicyId?: string | null,
) {
  return stepUpFetch<
    | { status: 'proposed'; proposal: AppRoleProposalDto }
    | {
        status: 'granted_single_approver';
        reason: AppRoleSingleApproverReasonDto;
        assignment: AppRoleAssignmentDto;
      }
  >(`${appAccessPath(scope.holderId, scope.archeId)}/proposals`, {
    method: 'POST',
    body: { ...body, ...expected(expectedAccessPolicyId), ...proof(creds) },
  });
}

export async function approveAppRoleProposal(
  scope: AppAccessScope,
  proposalId: string,
  creds: StepUpCredentials,
  /** The policy the screen showed. */
  expectedAccessPolicyId?: string | null,
) {
  return stepUpFetch<{ assignment: AppRoleAssignmentDto }>(
    `${appAccessPath(scope.holderId, scope.archeId)}/proposals/${encodeURIComponent(proposalId)}/approve`,
    { method: 'POST', body: { ...expected(expectedAccessPolicyId), ...proof(creds) } },
  );
}

/** Accept a SENSITIVE role's new permissions (a non-sensitive one uses `api`). */
export async function acceptSensitiveAppRoleAdditions(
  scope: AppAccessScope,
  roleId: string,
  creds: StepUpCredentials,
  /** The policy the screen showed — the additions it listed. */
  expectedAccessPolicyId?: string | null,
) {
  return stepUpFetch<{ accepted: string[] }>(
    `${appAccessPath(scope.holderId, scope.archeId)}/roles/${encodeURIComponent(roleId)}/accept`,
    { method: 'POST', body: { ...expected(expectedAccessPolicyId), ...proof(creds) } },
  );
}

/** Accept a non-sensitive role's new permissions — those the screen showed (`expectedAccessPolicyId`). */
export function useAcceptAppRoleAdditions(scope: AppAccessScope) {
  const invalidate = useInvalidateAppAccess(scope);
  return useMutation({
    mutationFn: busyRetrying(
      (input: string | { roleId: string; expectedAccessPolicyId?: string | null }) => {
        const { roleId, expectedAccessPolicyId } =
          typeof input === 'string' ? { roleId: input, expectedAccessPolicyId: null } : input;
        return expectedAccessPolicyId
          ? api.acceptAppRoleAdditions(
              scope.holderId,
              scope.archeId,
              roleId,
              expectedAccessPolicyId,
            )
          : api.acceptAppRoleAdditions(scope.holderId, scope.archeId, roleId);
      },
    ),
    onSuccess: invalidate,
    onError: (err) => {
      if (isAccessPolicyChanged(err)) void invalidate();
    },
  });
}

/**
 * Change the app's settings here. Tightening needs no proof; loosening
 * (turning a safeguard off) needs one — the server refuses it without
 * (`STEP_UP_REQUIRED`), and the caller then asks for one.
 */
export async function updateAppAccessSettings(
  scope: AppAccessScope,
  settings: Partial<AppAccessSettingsDto>,
  creds: StepUpCredentials | null,
) {
  const write = () =>
    stepUpFetch<{ settings: AppAccessSettingsDto }>(
      `/vault/holders/${encodeURIComponent(scope.holderId)}/app-access/settings`,
      {
        method: 'PUT',
        body: { archeId: scope.archeId, ...settings, ...(creds ? proof(creds) : {}) },
      },
    );
  // Only a write without proof is retried (see the header).
  return creds ? write() : retryOnceIfBusy(write);
}

/** Invalidate after a step-up write that bypassed react-query's mutation layer. */
export function useAppAccessInvalidator(scope: AppAccessScope) {
  return useInvalidateAppAccess(scope);
}
